import React from 'react';
import {Button, Card, Col, List, Row, Tag, Tooltip, Typography} from 'antd';
import dayjs from 'dayjs';

import {useGetJails} from '../../../hooks/useGetJails';
import {useHandleReleaseMutation} from '../../../store/api/jailApi';
import {
    selectAccountMembershipNumber,
    selectCurrentProfile,
    selectFullName,
    selectIsStaff
} from '../../../store/slices/userSlice';
import {useAppSelector} from '../../../hooks/useStore';
import {BaseError} from '../../../types/apiTypes';
import {RemoveRestrictionProps} from '../../../types/accountTypes';

import {dateTimeFormat} from '../../../lib/time';
import {messageType, showMessages} from '../../../helpers/message';
import confirmAction from '../../../helpers/confirmAction';

import {jailListGridStyle} from '../../../assets/styles';
import './JailRestrictions.less';

const {Paragraph, Text} = Typography;

const irrevocableJails = ['data pushed to v7', 'address mismatched', 'name mismatched'];

const RemoveRestrictionButton = ({confirm, action, hide}: RemoveRestrictionProps) => {
    if (hide) return null;
    return (
        <Button
            type="primary"
            className="btn-card"
            onClick={() => {
                confirm === null ?
                    action() :
                    confirmAction(
                        confirm.title,
                        confirm.description,
                        action,
                    );
            }}
        >
            Remove
        </Button>
    );
}

const JailMemberRestrictions = () => {
    const isStaff = useAppSelector(selectIsStaff);
    const profile = useAppSelector(selectCurrentProfile);
    const membershipNumber = useAppSelector(selectAccountMembershipNumber);
    const displayPerson = useAppSelector(selectFullName);

    const {jails, isJailsLoading} = useGetJails({isStaff, profile});
    const [releaseFromJail, {isError, error, isSuccess, isLoading, reset}] = useHandleReleaseMutation();

    React.useEffect(() => {
        if (isSuccess) {
            showMessages('Restriction has successfully been removed.', messageType.success);
            reset();
        }
        if (isError) {
            showMessages((error as BaseError).data?.message, messageType.error);
            reset();
        }
    }, [isSuccess, isError, error, reset]);

    return (
        <>
            <List
                className="restriction-list"
                grid={jailListGridStyle}
                dataSource={jails}
                loading={isJailsLoading || isLoading}
                renderItem={item => {
                    const startDate = dayjs(item.started_at).tz().format(dateTimeFormat);
                    const endDate = item.ended_at ? dayjs(item.ended_at).tz().format(dateTimeFormat) : 'forever';
                    const modelType = item.model_type.includes('Profile') ? 'profile' : 'account';

                    // List of jail.names that can not be revoked so hide the button.
                    const hideButton = irrevocableJails.includes(item.name);

                    let confirm = null;
                    switch (item.name) {
                        case 'abstract requires manual approval':
                            confirm = {
                                title: 'Are you sure?',
                                description: 'If the applicant has no other restrictions (ie CPH eligibility), an email will be sent notifying them of their approval.',
                            }
                            break;
                        case 'CPH program eligibility pending':
                            confirm = {
                                title: 'Are you sure?',
                                description: 'If the applicant has no other restrictions (ie driving eligibility), an email will be sent notifying them of their approval.',
                            }
                            break;
                        default:
                            break;
                    }

                    const action = () => releaseFromJail({
                        jailName: item.name,
                        modelType: modelType,
                        modelID: item.model_id,
                    });

                    return (
                        <List.Item>
                            <Card
                                title={
                                    <Tooltip title={item.friendly_name} placement="top">
                                        <span>{item.friendly_name}</span>
                                    </Tooltip>
                                }
                                className="restriction-card"
                            >
                                <Card.Meta
                                    description={
                                        <>
                                            <Row>
                                                <Col span={24}>
                                                    {
                                                        modelType === 'profile' ?
                                                            <Tag color="blue">
                                                                {membershipNumber} {displayPerson}
                                                            </Tag>
                                                        : null
                                                    }
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col span={24}><strong>Reason:</strong></Col>
                                                <Col span={24}>
                                                    <Text>{item.reason ?? 'N/A'}</Text>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col span={24}><strong>Release condition:</strong></Col>
                                                <Col span={24}>
                                                    <Paragraph>{item.bail_conditions ?? 'N/A'}</Paragraph>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col span={24}><strong>Time:</strong></Col>
                                                <Col span={24}>
                                                    <Text>{startDate} to {endDate}</Text>
                                                </Col>
                                            </Row>
                                            <RemoveRestrictionButton
                                                confirm={confirm}
                                                action={action}
                                                hide={hideButton}
                                            />
                                        </>
                                    }
                                />
                            </Card>
                        </List.Item>
                    )
                }}
            />
        </>
    );
}


export default JailMemberRestrictions;
